import type { PanelState } from "@/components/layout/shell/quicktask/types";

const get = (key: string): string | null => {
  try {
    const raw = localStorage.getItem(key);
    return raw !== null ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const set = (key: string, value: string): void => {
  localStorage.setItem(key, JSON.stringify(value));
};

const remove = (key: string): void => {
  localStorage.removeItem(key);
};

const KEYS = {
  panelState: "quickTasksWrapper",
  customerId: "quickTasksWrapper_customerID",
  customerName: "taskCustomerName",
  customerIdField: "taskCustomerId",
  contactId: "taskContactId",
  activity: "taskEditorActivity",
  subject: "taskEditorSubject",
  body: "taskEditorBody",
  projectName: "taskEditorProjectName",
  projectId: "taskEditorProjectId",
  taskId: "taskEditorHiddenID",
} as const;

export type StorageKey = (typeof KEYS)[keyof typeof KEYS];

export interface StoredFormValues {
  customerName: string;
  customerId: string;
  contactId: string;
  activityId: string;
  subject: string;
  body: string;
  projectName: string;
  projectId: string;
  taskId: string;
}

export const quickTaskStorage = () => {
  const getStoredPanelState = (): PanelState => {
    const value = get(KEYS.panelState) ?? "";
    if (value === "OPEN" || value === "MINIFIED") return value;
    return "CLOSED";
  };

  const getStoredFormValues = (): StoredFormValues => ({
    customerName: get(KEYS.customerName) ?? "",
    customerId: get(KEYS.customerIdField) ?? "",
    contactId: get(KEYS.contactId) ?? "",
    activityId: get(KEYS.activity) ?? "",
    subject: get(KEYS.subject) ?? "",
    body: get(KEYS.body) ?? "",
    projectName: get(KEYS.projectName) ?? "",
    projectId: get(KEYS.projectId) ?? "",
    taskId: get(KEYS.taskId) ?? "",
  });

  const saveFieldValue = (key: StorageKey, value: string): void => {
    set(key, value);
  };

  const savePanelState = (state: PanelState, customerId?: string): void => {
    if (state === "CLOSED") {
      remove(KEYS.panelState);
      remove(KEYS.customerId);
    } else {
      set(KEYS.panelState, state);
      if (customerId !== undefined) {
        set(KEYS.customerId, customerId);
      }
    }
  };

  const saveTaskId = (taskId: string): void => {
    set(KEYS.taskId, taskId);
  };

  const clearAll = (): void => {
    Object.values(KEYS).forEach(remove);
  };

  return {
    KEYS,
    getStoredPanelState,
    getStoredFormValues,
    saveFieldValue,
    savePanelState,
    saveTaskId,
    clearAll,
  };
};
